import { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  Loader2,
  MessageCircle,
  RefreshCw,
  Send,
  Eye,
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { apiFetch, apiGetJson, apiSendJson } from "@/lib/sm-api";
import { toast } from "sonner";

type MessagingOverview = {
  providers: Array<{ id: string; provider_type: string; environment: string; active: boolean }>;
  templates: Array<{ code: string; channel: string; active: boolean; body?: string; variables_schema?: unknown }>;
  outbound: Array<{
    id: string;
    tenant_id: string;
    template_code: string;
    recipient: string;
    status: string;
    provider_status: string | null;
    provider_message_id: string | null;
    provider: string;
    error_message: string | null;
    retry_count: number;
    retry_reason: string | null;
    estimated_cost: number | null;
    created_at: string;
    sent_at: string | null;
    delivered_at: string | null;
    failed_at: string | null;
    last_retry_at: string | null;
    rendered_message: string | null;
  }>;
  usage: Array<{ tenant_id: string; day: string; messages_sent: number; messages_failed: number }>;
  platformConfigured: boolean;
  envHints: Record<string, boolean>;
  diagnostics: {
    configured: boolean;
    trial: boolean;
    sandbox: boolean;
    production: boolean;
    mode: string;
    sender: string | null;
    messagingServiceSid: string | null;
    verifiedNumbersCount: number;
    sandboxJoinCode: string | null;
    sandboxInstructions: string;
    lastError: string | null;
  };
  delivery: {
    sent: number;
    delivered: number;
    failed: number;
    retried: number;
    retryRate: number;
    deliveryRate: number;
    estimatedCost: number;
    lastDelivery: { delivered_at?: string } | null;
    lastError: { error_message?: string; failed_at?: string } | null;
  };
  sandbox: { sender: string; joinCode: string | null; instructions: string };
};

type TenantOpt = { id: string; name: string; slug: string };

const TABS = [
  ["overview", "Overview"],
  ["diagnostics", "Diagnóstico"],
  ["sandbox", "Sandbox"],
  ["testing", "Testing"],
  ["templates", "Templates"],
  ["delivery", "Delivery"],
] as const;

type TabId = (typeof TABS)[number][0];

function statusBadge(status: string) {
  if (status === "delivered") return "default";
  if (status === "failed" || status === "undelivered") return "destructive";
  if (status === "sent") return "secondary";
  return "outline";
}

export function MessagingAdminPanel() {
  const [data, setData] = useState<MessagingOverview | null>(null);
  const [tenants, setTenants] = useState<TenantOpt[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<TabId>("overview");
  const [sending, setSending] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState<{
    renderedBody?: string;
    providerPayloadPreview?: Record<string, string>;
    variables?: Record<string, string>;
  } | null>(null);
  const [testResult, setTestResult] = useState<Record<string, unknown> | null>(null);

  const [testForm, setTestForm] = useState({
    tenantId: "",
    templateCode: "order_created_owner",
    phone: "",
    variablesJson: "{}",
  });

  const templateCodes = useMemo(
    () => data?.templates.map((t) => t.code) ?? ["order_created_owner"],
    [data?.templates],
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [res, ts] = await Promise.all([
        apiFetch("/api/admin/messaging"),
        apiGetJson<{ id: string; name: string; slug: string }[]>("/api/admin/tenants").catch(() => []),
      ]);
      if (!res.ok) throw new Error(await res.text());
      setData((await res.json()) as MessagingOverview);
      setTenants(ts.map((t) => ({ id: t.id, name: t.name, slug: t.slug })));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Error al cargar");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const runPreview = async (code?: string) => {
    const templateCode = code ?? testForm.templateCode;
    setPreviewing(true);
    try {
      let variables: Record<string, string> | undefined;
      try {
        const parsed = JSON.parse(testForm.variablesJson || "{}") as Record<string, string>;
        if (Object.keys(parsed).length) variables = parsed;
      } catch {
        toast.error("JSON de variables inválido");
        return;
      }
      const r = await apiSendJson<typeof preview>("/api/admin/messaging", "POST", {
        action: "preview",
        templateCode,
        variables,
      });
      setPreview(r);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Preview falló");
    } finally {
      setPreviewing(false);
    }
  };

  const sendTest = async () => {
    if (!testForm.tenantId || !testForm.phone) {
      toast.error("Tenant y teléfono requeridos");
      return;
    }
    setSending(true);
    setTestResult(null);
    try {
      let variables: Record<string, string> | undefined;
      try {
        const parsed = JSON.parse(testForm.variablesJson || "{}") as Record<string, string>;
        if (Object.keys(parsed).length) variables = parsed;
      } catch {
        toast.error("JSON de variables inválido");
        return;
      }
      const r = await apiSendJson<Record<string, unknown>>("/api/admin/messaging", "POST", {
        action: "test",
        tenantId: testForm.tenantId,
        templateCode: testForm.templateCode,
        phone: testForm.phone,
        variables,
      });
      setTestResult(r);
      if (r.ok) toast.success("Mensaje de prueba enviado (pipeline real)");
      else toast.error(String(r.error ?? r.skipped ?? "Envío fallido"));
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Error");
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Cargando mensajería…
      </div>
    );
  }

  if (error) return <p className="text-sm text-destructive">{error}</p>;
  if (!data) return null;

  const d = data.diagnostics;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-2">
        <MessageCircle className="h-5 w-5 text-primary" />
        <div className="flex-1 min-w-[200px]">
          <h2 className="text-lg font-semibold">Mensajería transaccional</h2>
          <p className="text-sm text-muted-foreground">
            Provider-agnostic · sent ≠ delivered · docs/TWILIO_MESSAGING_SETUP.md
          </p>
        </div>
        <Badge variant={data.platformConfigured ? "default" : "destructive"}>
          {d.mode.toUpperCase()}
        </Badge>
        <Button variant="outline" size="sm" onClick={() => void load()} className="gap-1">
          <RefreshCw className="h-3.5 w-3.5" /> Actualizar
        </Button>
      </div>

      <div className="flex flex-wrap gap-2 rounded-lg border bg-muted/30 p-1">
        {TABS.map(([id, label]) => (
          <button
            key={id}
            type="button"
            onClick={() => setTab(id)}
            className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
              tab === id ? "bg-background shadow-sm" : "text-muted-foreground hover:text-foreground"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <div className="grid gap-4 md:grid-cols-3">
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm">Delivery (30d)</CardTitle></CardHeader>
            <CardContent className="text-sm space-y-1">
              <div className="flex justify-between"><span>Enviados</span><span>{data.delivery.sent}</span></div>
              <div className="flex justify-between"><span>Entregados</span><span className="text-green-600">{data.delivery.delivered}</span></div>
              <div className="flex justify-between"><span>Fallidos</span><span className="text-destructive">{data.delivery.failed}</span></div>
              <div className="flex justify-between text-muted-foreground"><span>Delivery %</span><span>{(data.delivery.deliveryRate * 100).toFixed(1)}%</span></div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm">Modo Twilio</CardTitle></CardHeader>
            <CardContent className="text-sm space-y-1">
              <div className="flex justify-between"><span>Trial</span><span>{d.trial ? "sí" : "no"}</span></div>
              <div className="flex justify-between"><span>Sandbox</span><span>{d.sandbox ? "activo" : "no"}</span></div>
              <div className="flex justify-between"><span>Producción</span><span>{d.production ? "activo" : "no"}</span></div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm">ENV</CardTitle></CardHeader>
            <CardContent className="text-xs space-y-1">
              {Object.entries(data.envHints).map(([k, v]) => (
                <div key={k} className="flex justify-between"><span>{k}</span><span>{v ? "✓" : "—"}</span></div>
              ))}
            </CardContent>
          </Card>
        </div>
      )}

      {tab === "diagnostics" && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Diagnóstico provider</CardTitle>
            <CardDescription>Sin secrets en logs · trial/sandbox/prod</CardDescription>
          </CardHeader>
          <CardContent className="text-sm">
            <table className="w-full">
              <tbody>
                {[
                  ["Trial", d.trial ? "sí" : "no"],
                  ["Sandbox", d.sandbox ? "activo" : "no"],
                  ["Production", d.production ? "activo" : "no"],
                  ["Sender", d.sender ?? "—"],
                  ["Messaging Service", d.messagingServiceSid ?? "—"],
                  ["Números verificados", String(d.verifiedNumbersCount)],
                  ["Última entrega", data.delivery.lastDelivery?.delivered_at ?? "—"],
                  ["Último error", data.delivery.lastError?.error_message ?? d.lastError ?? "—"],
                ].map(([k, v]) => (
                  <tr key={k} className="border-t border-border/40">
                    <td className="py-2 pr-4 text-muted-foreground">{k}</td>
                    <td className="py-2 font-mono text-xs break-all">{v}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {tab === "sandbox" && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Sandbox WhatsApp</CardTitle>
            <CardDescription>Cuenta trial: obligatorio para pruebas</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <p><strong>Sender sandbox:</strong> <code className="text-xs">{data.sandbox.sender}</code></p>
            <p><strong>Join code:</strong> {data.sandbox.joinCode ?? "Configurá TWILIO_SANDBOX_JOIN_CODE o ver consola Twilio"}</p>
            <p className="text-muted-foreground">{data.sandbox.instructions}</p>
            <ol className="list-decimal pl-5 space-y-1 text-muted-foreground">
              <li>Enviá join al sandbox desde tu WhatsApp</li>
              <li>Usá el Testing panel con tu número verificado</li>
              <li>Webhook: POST /api/messaging/webhook/twilio</li>
            </ol>
          </CardContent>
        </Card>
      )}

      {tab === "testing" && (
        <div className="grid gap-4 lg:grid-cols-2">
          <Card>
            <CardHeader><CardTitle className="text-sm flex items-center gap-2"><Send className="h-4 w-4" /> Send Test (pipeline real)</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div><Label className="text-xs">Tenant</Label>
                <select className="mt-1 h-9 w-full rounded-md border bg-background px-2 text-sm" value={testForm.tenantId}
                  onChange={(e) => setTestForm((f) => ({ ...f, tenantId: e.target.value }))}>
                  <option value="">Elegí tenant…</option>
                  {tenants.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
              <div><Label className="text-xs">Template</Label>
                <select className="mt-1 h-9 w-full rounded-md border bg-background px-2 text-sm" value={testForm.templateCode}
                  onChange={(e) => setTestForm((f) => ({ ...f, templateCode: e.target.value }))}>
                  {templateCodes.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div><Label className="text-xs">Teléfono E.164 (destino verificado / sandbox join)</Label>
                <Input value={testForm.phone} onChange={(e) => setTestForm((f) => ({ ...f, phone: e.target.value }))} placeholder="+54911..." />
              </div>
              <div><Label className="text-xs">Variables JSON (opcional)</Label>
                <Textarea rows={3} value={testForm.variablesJson} onChange={(e) => setTestForm((f) => ({ ...f, variablesJson: e.target.value }))} className="font-mono text-xs" />
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => void runPreview()} disabled={previewing} className="gap-1">
                  {previewing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />} Preview
                </Button>
                <Button onClick={() => void sendTest()} disabled={sending} className="gap-1 flex-1">
                  {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send Test
                </Button>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle className="text-sm">Resultado</CardTitle></CardHeader>
            <CardContent className="space-y-3 text-xs font-mono">
              {preview && (
                <div className="rounded border p-2 bg-muted/30">
                  <p className="font-sans text-xs font-semibold mb-1">Rendered</p>
                  <pre className="whitespace-pre-wrap">{preview.renderedBody}</pre>
                </div>
              )}
              {testResult && (
                <pre className="whitespace-pre-wrap overflow-auto max-h-80">{JSON.stringify(testResult, null, 2)}</pre>
              )}
              {!preview && !testResult && <p className="text-muted-foreground font-sans">Preview o Send Test para ver resultado</p>}
            </CardContent>
          </Card>
        </div>
      )}

      {tab === "templates" && (
        <div className="space-y-3">
          {data.templates.map((t) => (
            <Card key={t.code}>
              <CardHeader className="pb-2 flex flex-row items-center justify-between">
                <CardTitle className="text-sm">{t.code}</CardTitle>
                <Button size="sm" variant="outline" onClick={() => { setTestForm((f) => ({ ...f, templateCode: t.code })); void runPreview(t.code); setTab("templates"); }}>
                  Preview mock
                </Button>
              </CardHeader>
              <CardContent className="text-xs text-muted-foreground">
                Variables: {JSON.stringify(t.variables_schema)}
              </CardContent>
            </Card>
          ))}
          {preview && tab === "templates" && (
            <Card><CardContent className="pt-4"><pre className="text-xs whitespace-pre-wrap">{preview.renderedBody}</pre></CardContent></Card>
          )}
        </div>
      )}

      {tab === "delivery" && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm flex items-center gap-2">
              Delivery timeline
              <Badge variant="outline" className="font-normal">sent ≠ delivered</Badge>
            </CardTitle>
            <CardDescription>Retries: metadata only (sin queue engine)</CardDescription>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-muted-foreground">
                  <th className="p-2">Estado</th>
                  <th className="p-2">Provider</th>
                  <th className="p-2">SID</th>
                  <th className="p-2">Retry</th>
                  <th className="p-2">Sent</th>
                  <th className="p-2">Delivered</th>
                  <th className="p-2">Destino</th>
                </tr>
              </thead>
              <tbody>
                {data.outbound.map((m) => (
                  <tr key={m.id} className="border-t border-border/40">
                    <td className="p-2"><Badge variant={statusBadge(m.status)}>{m.status}</Badge></td>
                    <td className="p-2">{m.provider_status ?? "—"}</td>
                    <td className="p-2 font-mono">{m.provider_message_id?.slice(0, 10) ?? "—"}</td>
                    <td className="p-2">{m.retry_count}{m.retry_reason ? ` · ${m.retry_reason.slice(0, 30)}` : ""}</td>
                    <td className="p-2">{m.sent_at ? new Date(m.sent_at).toLocaleString("es-AR") : "—"}</td>
                    <td className="p-2">{m.delivered_at ? <CheckCircle2 className="h-3.5 w-3.5 text-green-600 inline" /> : "—"}</td>
                    <td className="p-2">{m.recipient}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {data.delivery.retried > 0 && (
              <p className="mt-3 text-xs text-muted-foreground flex items-center gap-1">
                <AlertTriangle className="h-3.5 w-3.5" />
                Retry rate 30d: {(data.delivery.retryRate * 100).toFixed(1)}%
              </p>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
